import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { PPEData } from '../types/ppe';

interface CertificatePageProps {
  data: PPEData;
}

// Risk Meter Component
const RiskMeter: React.FC<{ riskLevel: string }> = ({ riskLevel }) => {
  const getMeterValue = (level: string) => {
    switch (level) {
      case 'I': return 0; // Green - Low risk
      case 'II': return 1; // Yellow - Medium risk  
      case 'III': return 2; // Red - High risk
      default: return 1;
    }
  };



  const meterValue = getMeterValue(riskLevel);
  // Calculate pointer position: 0 = left (Low), 1 = center (Medium), 2 = right (High)
  // Keep pointer within 15% to 85% of the bar for better visibility
  const pointerPosition = 15 + (meterValue * 35); // 15%, 50%, 85%

  return (
    <div className="text-center mb-4">
      
      {/* Horizontal Bar */}
      <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden mb-3">
        {/* Green segment (Low Risk) */}
        <div className="absolute left-0 top-0 w-1/3 h-full bg-green-500"></div>
        
        {/* Yellow segment (Medium Risk) */}
        <div className="absolute left-1/3 top-0 w-1/3 h-full bg-yellow-500"></div>
        
        {/* Red segment (High Risk) */}
        <div className="absolute right-0 top-0 w-1/3 h-full bg-red-500"></div>
        
        {/* Black pointer */}
        <div 
          className="absolute top-0 w-2 h-full bg-black transform -translate-x-1/2"
          style={{ left: `${pointerPosition}%` }}
        ></div>
      </div>

    
    </div>
  );
};

const CertificatePage: React.FC<CertificatePageProps> = ({ data }) => {
  const { certificateId } = useParams<{ certificateId: string }>();
  
  // Find the certificate by ID
  let certificate = null;
  let category = null;
  
  for (const cat of data.categories) {
    const found = cat.certificates.find(cert => cert.id === certificateId);
    if (found) {
      certificate = found;
      category = cat;
      break;
    }
  }
  
  if (!certificate || !category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-4">Certificate Not Found</h1>
          <Link to="/" className="text-brand-yellow hover:text-primary-600 transition-colors duration-200">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'I': return 'bg-green-500';
      case 'II': return 'bg-yellow-500';
      case 'III': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getRiskLevelName = (level: string) => {
    return data.riskLevels[level]?.name || `Category ${level}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-brand-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
            <Link to="/" className="hover:text-brand-yellow transition-colors duration-200">Home</Link>
            <span>/</span>
            <Link to={`/category/${category.id}`} className="hover:text-brand-yellow transition-colors duration-200">{category.name}</Link>
            <span>/</span>
            <span className="text-black font-medium">{certificate.code}</span>
          </nav>
          
          <div className="flex items-start space-x-6">
            
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                <h1 className="text-6xl font-bold text-black">{certificate.code}</h1>
                
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">{certificate.name}</h2>
              <p className="text-lg text-gray-600 max-w-3xl">{certificate.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Details */}
            <div className="card">
              <h3 className="text-xl font-bold text-black mb-4">Certificate Details</h3>
              <p className="text-gray-700 leading-relaxed">{certificate.details}</p>
            </div>

            {/* Examples */}
            {certificate.examples.length > 0 && (
              <div className="card">
                <h3 className="text-xl font-bold text-black mb-4">Examples of Use</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {certificate.examples.map((example, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-brand-yellow rounded-full"></div>
                      <span className="text-gray-700">{example}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Products */}
            {certificate.products.length > 0 && (
              <div className="card">
                <h3 className="text-xl font-bold text-black mb-4">Available Products</h3>
                <div className="space-y-4">
                  {certificate.products.map((product, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-brand-yellow transition-colors duration-200">
                      <div className="flex items-start space-x-4">
                        {product.imageUrl && (
                          <div className="flex-shrink-0">
                            <img 
                              src={product.imageUrl} 
                              alt={product.name}
                              className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-semibold text-black">{product.name}</h4>
                            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {product.certification}
                            </span>
                          </div>
                          <a
                            href={product.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 bg-brand-yellow hover:bg-primary-600 text-black font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                          >
                            <span>View Product</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* View All Products Button */}
                <div className="mt-6 text-center">
                  <a
                    href={`https://www.safety-workwear-shop.com/en/search/${encodeURIComponent(certificate.code)}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                    <span>View All Products</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Risk Level Info */}
            <div className="card">
              
              {/* Risk Meter */}
              <RiskMeter riskLevel={certificate.riskLevel} />
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${getRiskLevelColor(certificate.riskLevel)}`}></div>
                  <span className="text-gray-700 font-medium">
                    {getRiskLevelName(certificate.riskLevel)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {data.riskLevels[certificate.riskLevel]?.description || 'Risk level information not available.'}
                </p>
              </div>
            </div>

            {/* Category Info */}
            <div className="card">
              <h3 className="text-lg font-bold text-black mb-3">Category</h3>
              <Link 
                to={`/category/${category.id}`}
                className="inline-flex items-center space-x-2 text-brand-yellow hover:text-primary-600 transition-colors duration-200 font-medium"
              >
                <span>{category.name}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <p className="text-sm text-gray-600 mt-2">{category.description}</p>
            </div>

            {/* Safety Workwear Shop Link */}
            <div className="card bg-black text-white">
              <div className="text-center">
                <img 
                  src="/sws-logo.png" 
                  alt="Safety Workwear Shop" 
                  className="h-12 w-auto mx-auto mb-3"
                />
                <h3 className="text-lg font-bold mb-2">Need PPE Products?</h3>
                <p className="text-sm text-gray-300 mb-4">
                  Visit our main shop for certified PPE equipment
                </p>
                <a
                  href="https://www.safety-workwear-shop.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-brand-yellow hover:bg-primary-600 text-black font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Visit Shop
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex flex-col sm:flex-row justify-between items-center">
          <Link 
            to={`/category/${category.id}`}
            className="inline-flex items-center space-x-2 text-brand-yellow hover:text-primary-600 transition-colors duration-200 font-medium mb-4 sm:mb-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to {category.name}</span>
          </Link>
          
          <Link 
            to="/search"
            className="inline-flex items-center space-x-2 bg-brand-yellow hover:bg-primary-600 text-black font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            <span>Search More Certificates</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Powered by Section */}
      <div className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3">
            <span className="text-gray-400">PPE Certificate Portal powered by</span>
            <img 
              src="/sws-logo.png" 
              alt="Safety Workwear Shop" 
              className="h-8 w-auto opacity-80"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;
